import { RefObject, useEffect, useState } from "react";

import { OUTCOMES_TOUR_TARGET_POLL_MS, OUTCOMES_TOUR_TARGET_WAIT_MS, OutcomesTourStep } from "consts";

import { findTourTarget } from "pages/app/scenario-analysis/utils/outcomesTourTarget";

// The area found for a step. Tied to its step, so a new step never measures the last one's area.
interface TargetLookup {
  step: OutcomesTourStep;
  element: HTMLElement | null;
  hasGivenUp: boolean;
}

/** Finds the step's area, all step long: it can render late (another tab) or remount (a loader giving way to the table). */
export const useOutcomesTourTarget = (rootRef: RefObject<HTMLElement>, step: OutcomesTourStep) => {
  const [lookup, setLookup] = useState<TargetLookup | null>(null);

  useEffect(() => {
    const startedAt = Date.now();
    const lookForTarget = () => {
      const found = findTourTarget(rootRef.current, step.target);
      const hasWaited = Date.now() - startedAt >= OUTCOMES_TOUR_TARGET_WAIT_MS;
      setLookup((previous) => {
        const isSameStep = previous?.step === step;
        if (found) return isSameStep && previous.element === found ? previous : { step, element: found, hasGivenUp: false };
        if (hasWaited) return isSameStep && previous.hasGivenUp ? previous : { step, element: null, hasGivenUp: true };
        if (isSameStep && previous.element) return { step, element: null, hasGivenUp: false };
        return previous;
      });
    };
    lookForTarget();
    const interval = setInterval(lookForTarget, OUTCOMES_TOUR_TARGET_POLL_MS);
    return () => clearInterval(interval);
  }, [step, rootRef]);

  const isForStep = lookup?.step === step;
  return {
    target: isForStep ? lookup.element : null,
    hasGivenUpOnTarget: isForStep && lookup.hasGivenUp,
    /** Found earlier this step and gone now, such as a table replaced by a loader. */
    hasLostTarget: isForStep && !lookup.element && !lookup.hasGivenUp,
  };
};
